import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TablePagination,
  Toolbar,
  Typography
} from '@mui/material';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';

import { Document, DocumentSortBy, SearchResult } from '../api/ApiClient';
import DocumentPermissions from '../components/documents/DocumentPermissions';
import BulkPermissionsDialog, { type BulkPermissionsChange } from '../components/documents/BulkPermissionsDialog';
import type { BulkDocumentsResponse } from '../api/data-contracts';
import DocumentSources from '../components/documents/DocumentSources';
import { apiClient } from '../api';
import { log } from '../utils/errors/logger';
import AuthService from '../services/AuthService';

// New modular components
import {
  DocumentSearchBar,
  DocumentTabs,
  DocumentTable,
  DocumentDialog,
  DeleteConfirmationDialog,
  DocumentFilterBar,
  DateRangeFilters
} from '../components/documents';

import { 
  useDocuments, 
  useCreateDocument,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument,
  useReprocessDocument,
  useDeleteDocumentsBulk,
  useUpdateDocumentsPermissionsBulk
} from '../hooks/api/useDocuments';
import { useMyGroupsSimple } from '../hooks/api/useGroups';

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [visibility, setVisibility] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [dateFilters, setDateFilters] = useState<DateRangeFilters>({
    createdFrom: null,
    createdTo: null,
    updatedFrom: null,
    updatedTo: null
  });
  const [sharedWithGroup, setSharedWithGroup] = useState<string>('');
  const [sharedWithUser, setSharedWithUser] = useState<string>('');

  // Get current user
  const user = AuthService.getUser();
  const canAddCustomDocs = Boolean(user);
  // The user's own groups tell a direct share from a share through a group
  const { data: myGroups } = useMyGroupsSimple();
  const currentUserGroupIds = useMemo(() => (myGroups ?? []).map((group) => group.id), [myGroups]);

  // Get source type filter based on tab value
  const getSourceTypeFilter = () => {
    if (tabValue === 3) {return 'sap';}
    if (tabValue === 4) {return 'custom';}
    return undefined; // All documents
  };

  // Get owner ID filter based on tab value
  const getOwnerIdFilter = () => {
    if (tabValue === 1) {return user?.id;} // My Documents tab
    return undefined;
  };

  // Get exclude_owned filter based on tab value
  const getExcludeOwnedFilter = () => {
    if (tabValue === 2) {return true;} // Shared with Me tab
    return undefined;
  };

  // Convert dates to ISO format for API
  const formatDateForAPI = (date: Date | null): string | undefined => {
    if (!date) {return undefined;}
    return date.toISOString();
  };

  // TanStack Query hooks with server-side pagination and filtering
  const {
    data,
    isLoading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments
  } = useDocuments({
    skip: page * rowsPerPage,
    limit: rowsPerPage,
    source_type: getSourceTypeFilter(),
    visibility: visibility !== 'all' ? visibility : undefined,
    sort_by: sortBy as DocumentSortBy,
    created_at_gte: formatDateForAPI(dateFilters.createdFrom),
    created_at_lte: formatDateForAPI(dateFilters.createdTo),
    updated_at_gte: formatDateForAPI(dateFilters.updatedFrom),
    updated_at_lte: formatDateForAPI(dateFilters.updatedTo),
    shared_with_group: sharedWithGroup || undefined,
    shared_with_user: sharedWithUser || undefined,
    owner_id: getOwnerIdFilter(),
    exclude_owned: getExcludeOwnedFilter()
  }, {
    queryOptions: {
      // Poll while any listed document is still being processed so the
      // status chip flips to done (or failed) without a manual reload.
      refetchInterval: (query) => {
        const docs = query.state.data?.documents ?? [];
        const busy = docs.some((d) => d.status === 'pending' || d.status === 'processing');
        return busy ? 5000 : false;
      },
    },
  });

  const documents = data?.documents || [];
  const totalDocuments = data?.total || 0;
  
  // Mutations
  const createDocumentMutation = useCreateDocument();
  const uploadDocumentMutation = useUploadDocument();
  const updateDocumentMutation = useUpdateDocument();
  const deleteDocumentMutation = useDeleteDocument();
  const reprocessDocumentMutation = useReprocessDocument();
  const deleteDocumentsBulk = useDeleteDocumentsBulk();
  const updatePermissionsBulk = useUpdateDocumentsPermissionsBulk();

  const handleReprocess = useCallback((doc: { document_id: string }) => {
    reprocessDocumentMutation
      .mutateAsync(doc.document_id)
      .then(() => showInfo(t('documents.notification.reprocessStarted')))
      .catch((error: unknown) => {
        log.error('Error requesting document reprocessing', { error });
        showError(t('documents.notification.reprocessFailed'));
      });
  }, [reprocessDocumentMutation, showInfo, showError, t]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [openPermissionsDialog, setOpenPermissionsDialog] = useState(false);
  const [permissionsDocId, setPermissionsDocId] = useState<string>('');
  const [permissionsDocTitle, setPermissionsDocTitle] = useState<string>('');

  // Bulk selection. Lives on the page (not in the table) so the action bar,
  // the dialogs and the table share one set of ids.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const [openBulkPermissions, setOpenBulkPermissions] = useState(false);

  // Provider state
  const [availableProviders, setAvailableProviders] = useState<Array<{ value: string; label: string }>>([
    { value: 'local', label: 'Local' }
  ]);
  const [defaultProvider, setDefaultProvider] = useState<string>('local');

  // Helper function to check if user can edit a specific document
  const canEditDocument = (doc: Document | SearchResult) => {
    if (!user) {return false;}
    // Users can edit their own documents (custom, text, etc.) but not SAP documentation
    // Admins CANNOT edit other users' private documents
    const isOwner = 'user_id' in doc && doc.user_id === user.id;
    const isSap = 'source_type' in doc && doc.source_type === 'sap';
    return !isSap && isOwner;
  };

  // Helper function to check if user can delete a specific document
  const canDeleteDocument = (doc: Document | SearchResult) => {
    if (!user) {return false;}
    // Users can delete their own documents (custom, text, etc.) but not SAP documentation
    // Admins CANNOT delete other users' private documents
    const isOwner = 'user_id' in doc && doc.user_id === user.id;
    const isSap = 'source_type' in doc && doc.source_type === 'sap';
    return !isSap && isOwner;
  };
  
  // Note: This component uses DocumentTable and other sub-components
  // that handle API calls through the configured apiClient

  // Load text extraction providers
  const loadTextExtractionProviders = useCallback(async () => {
    try {
      // Note: API client structure might need adjustment based on actual API
      setAvailableProviders([{ value: 'local', label: 'Local' }]);
    } catch (error) {
      log.error('Error loading text extraction providers', { error });
    }
  }, []);

  // Load default text extraction configuration
  const loadDefaultTextExtractionConfig = useCallback(async () => {
    try {
      // Note: API client structure might need adjustment based on actual API
      const provider = 'local';
      setDefaultProvider(provider);
    } catch (error) {
      log.error('Error loading default text extraction config', { error });
    }
  }, []);

  // Server-side filtering via API - no need for client-side filtering
  const filteredDocuments = useMemo(() => {
    // Tab 5 is for Document Sources, return empty array
    if (tabValue === 5) {
      return [];
    }

    // If search is active and has results, convert and show search results
    if (searchResults.length > 0) {return searchResults;}

    return documents;
  }, [documents, tabValue, searchResults]);
  
  // Initial load of providers and config
  useEffect(() => {
    loadTextExtractionProviders();
    loadDefaultTextExtractionConfig();
  }, [loadTextExtractionProviders, loadDefaultTextExtractionConfig]);

  // Clear search results when query becomes empty
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Handle tab change - reset page when changing tabs
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  // Handle pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Handle filter changes
  const handleVisibilityChange = (value: string) => {
    setVisibility(value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSortByChange = (value: string) => {
    setSortBy(value);
    setPage(0); // Reset to first page when sort changes
  };

  const handleDateFiltersChange = (filters: DateRangeFilters) => {
    setDateFilters(filters);
    setPage(0); // Reset to first page when date filters change
  };

  const handleSharedWithGroupChange = (groupId: string) => {
    setSharedWithGroup(groupId);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSharedWithUserChange = (userId: string) => {
    setSharedWithUser(userId);
    setPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setVisibility('all');
    setSortBy('newest');
    setDateFilters({
      createdFrom: null,
      createdTo: null,
      updatedFrom: null,
      updatedTo: null
    });
    setSharedWithGroup('');
    setSharedWithUser('');
    setPage(0);
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await apiClient.searchDocuments({
        query: searchQuery,
        limit: 100,
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      log.error('Error searching documents', { error });
      showError(t('documents.search.failed'));
    } finally {
      setSearching(false);
    }
  };

  // Open document dialog
  const handleOpenDialog = (doc?: Document | SearchResult | null) => {
    if (doc && 'created_at' in doc && 'updated_at' in doc) {
      setEditingDoc(doc as Document);
    } else {
      setEditingDoc(null);
    }
    setOpenDialog(true);
  };

  // Submit document form
  const handleSubmitDocument = async (docData: any, isEdit: boolean) => {
    try {
      if (isEdit && editingDoc) {
        await updateDocumentMutation.mutateAsync({
          documentId: editingDoc.document_id,
          data: docData
        });
        showSuccess(t('documents.notification.updated'));
      } else {
        await createDocumentMutation.mutateAsync(docData);
        showSuccess(t('documents.notification.created'));
      }
    } catch (error) {
      log.error('Error submitting document', { error });
      showError(t('documents.notification.error'));
      throw error;
    }
  };

  const toggleSelection = useCallback((docId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) { next.delete(docId); } else { next.add(docId); }
      return next;
    });
  }, []);

  const toggleAllSelectable = useCallback((docIds: string[], select: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      docIds.forEach((id) => { if (select) { next.add(id); } else { next.delete(id); } });
      return next;
    });
  }, []);

  const leaveSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  /** One message for a bulk result: all done, or how many were refused and why. */
  const reportBulk = (result: BulkDocumentsResponse, doneKey: string) => {
    if (result.failed === 0) {
      showSuccess(t(doneKey, { count: result.succeeded }));
      return;
    }
    const reasons = result.results.filter((r) => !r.success).map((r) => r.error ?? 'failed');
    const forbidden = reasons.filter((r) => r === 'forbidden').length;
    showWarning(t('documents.bulk.partial', { done: result.succeeded, failed: result.failed, forbidden }));
  };

  // The server sends the same 1-100 bound as the conversation bulk delete;
  // a larger selection goes in blocks.
  const BULK_BLOCK = 100;
  const inBlocks = (ids: string[]) => {
    const blocks: string[][] = [];
    for (let i = 0; i < ids.length; i += BULK_BLOCK) { blocks.push(ids.slice(i, i + BULK_BLOCK)); }
    return blocks;
  };

  const mergeResults = (parts: BulkDocumentsResponse[]): BulkDocumentsResponse => ({
    results: parts.flatMap((p) => p.results),
    succeeded: parts.reduce((n, p) => n + p.succeeded, 0),
    failed: parts.reduce((n, p) => n + p.failed, 0),
  });

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      const parts = [];
      for (const block of inBlocks(ids)) {
        parts.push(await deleteDocumentsBulk.mutateAsync({ document_ids: block }));
      }
      reportBulk(mergeResults(parts), 'documents.bulk.deleted');
      setOpenBulkDelete(false);
      leaveSelectionMode();
    } catch (error) {
      log.error('Bulk delete failed', { error });
      showError(t('documents.notification.deleteFailed'));
    }
  };

  const handleBulkPermissions = async (change: BulkPermissionsChange) => {
    const ids = Array.from(selectedIds);
    try {
      const parts = [];
      for (const block of inBlocks(ids)) {
        parts.push(await updatePermissionsBulk.mutateAsync({ ...change, document_ids: block }));
      }
      reportBulk(mergeResults(parts), 'documents.bulk.permissionsDone');
      setOpenBulkPermissions(false);
      leaveSelectionMode();
    } catch (error) {
      log.error('Bulk permissions failed', { error });
      showError(t('documents.bulk.permissionsFailed'));
    }
  };

  // Handle delete document
  const handleDeleteDocument = async () => {
    if (!deleteDocId) {return;}

    try {
      await deleteDocumentMutation.mutateAsync(deleteDocId);
      showSuccess(t('documents.notification.deleteSuccess'));
      setOpenDeleteDialog(false);
    } catch (error) {
      log.error('Error deleting document', { error });
      showError(t('documents.notification.deleteFailed'));
    }
  };

  // Open delete confirmation dialog
  const confirmDelete = (docId: string) => {
    setDeleteDocId(docId);
    setOpenDeleteDialog(true);
  };

  // Open permissions dialog
  const openPermissions = (doc: Document | SearchResult) => {
    setPermissionsDocId(doc.document_id || '');
    setPermissionsDocTitle(doc.title || '');
    setOpenPermissionsDialog(true);
  };

  // Handle permissions dialog close
  const handlePermissionsClose = () => {
    setOpenPermissionsDialog(false);
    setPermissionsDocId('');
    setPermissionsDocTitle('');
  };

  // Handle permissions updated
  const handlePermissionsUpdated = () => {
    refetchDocuments();
    showSuccess(t('documents.permissions.updated'));
  };

  return (
    <>
      <PageLayout
        title={t('documents.title')}
        isLoading={false}
        maxWidth="lg"
      >
        {/* Search Bar */}
        <DocumentSearchBar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onSearch={handleSearch}
          onAddDocument={() => handleOpenDialog()}
          searching={searching}
          canAddCustomDocs={canAddCustomDocs}
        />

        {/* Document Tabs */}
        <DocumentTabs tabValue={tabValue} onTabChange={handleTabChange} />

        {/* Filter Bar - only show when not on Document Sources tab */}
        {tabValue !== 5 && (
          <DocumentFilterBar
            visibility={visibility}
            sortBy={sortBy}
            dateFilters={dateFilters}
            sharedWithGroup={sharedWithGroup}
            sharedWithUser={sharedWithUser}
            onVisibilityChange={handleVisibilityChange}
            onSortByChange={handleSortByChange}
            onDateFiltersChange={handleDateFiltersChange}
            onSharedWithGroupChange={handleSharedWithGroupChange}
            onSharedWithUserChange={handleSharedWithUserChange}
            onClearFilters={handleClearFilters}
          />
        )}

        {/* Bulk selection bar: only on the document tabs, only for users who may edit at all */}
        {tabValue !== 5 && canAddCustomDocs && (
          <Toolbar disableGutters variant="dense" sx={{ gap: 1, mb: 1, minHeight: 40 }}>
            {selectionMode ? (
              <>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {t('documents.bulk.selected', { count: selectedIds.size })}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" disabled={selectedIds.size === 0} onClick={() => setOpenBulkPermissions(true)}>
                    {t('documents.bulk.editPermissions')}
                  </Button>
                  <Button size="small" variant="outlined" color="error" disabled={selectedIds.size === 0} onClick={() => setOpenBulkDelete(true)}>
                    {t('documents.bulk.delete')}
                  </Button>
                  <Button size="small" onClick={leaveSelectionMode}>{t('common.cancel')}</Button>
                </Stack>
              </>
            ) : (
              <Button size="small" onClick={() => setSelectionMode(true)} sx={{ ml: 'auto' }}>
                {t('documents.bulk.select')}
              </Button>
            )}
          </Toolbar>
        )}

        {/* Document List */}
        {tabValue === 5 ? (
          <DocumentSources />
        ) : documentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <DocumentTable
              documents={filteredDocuments}
              canEditDocument={canEditDocument}
              canDeleteDocument={canDeleteDocument}
              onEdit={handleOpenDialog}
              onDelete={confirmDelete}
              onManagePermissions={openPermissions}
              onReprocess={handleReprocess}
              currentUserId={user?.id ?? null}
              currentUserGroupIds={currentUserGroupIds}
              selectedIds={selectionMode ? selectedIds : undefined}
              onToggleSelection={selectionMode ? toggleSelection : undefined}
              onToggleAllSelectable={selectionMode ? toggleAllSelectable : undefined}
              onShowSnackbar={(message, severity) => {
                switch (severity) {
                  case 'success': showSuccess(message); break;
                  case 'error': showError(message); break;
                  case 'warning': showWarning(message); break;
                  case 'info': showInfo(message); break;
                }
              }}
            />
            {/* Hide pagination when showing search results */}
            {searchResults.length === 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalDocuments}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Paper>
        )}
      </PageLayout>

      {/* Document Dialog */}
      <DocumentDialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); setEditingDoc(null); }}
        editingDoc={editingDoc}
        onSubmit={handleSubmitDocument}
        availableProviders={availableProviders}
        defaultProvider={defaultProvider}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteDocument}
      />

      {/* Bulk dialogs */}
      <DeleteConfirmationDialog
        open={openBulkDelete}
        onClose={() => setOpenBulkDelete(false)}
        onConfirm={() => { void handleBulkDelete(); }}
        title={t('documents.bulk.deleteTitle', { count: selectedIds.size })}
        message={t('documents.bulk.deleteMessage', { count: selectedIds.size })}
        loading={deleteDocumentsBulk.isPending}
      />
      <BulkPermissionsDialog
        open={openBulkPermissions}
        count={selectedIds.size}
        loading={updatePermissionsBulk.isPending}
        onClose={() => setOpenBulkPermissions(false)}
        onApply={(change) => { void handleBulkPermissions(change); }}
      />

      {/* Document Permissions Dialog */}
      {permissionsDocId && (
        <DocumentPermissions
          open={openPermissionsDialog}
          onClose={handlePermissionsClose}
          documentId={permissionsDocId}
          documentTitle={permissionsDocTitle}
          onPermissionsUpdated={handlePermissionsUpdated}
        />
      )}
    </>
  );
};

export default Documents;
